import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
} from "@mui/material";
import type { TestResult } from "./types";

interface RegexFormDialogProps {
  open: boolean;
  isEditMode: boolean;
  expression: string;
  testValue: string;
  testResult: TestResult | null;
  isTestSuccessful: boolean;
  isTesting: boolean;
  isSaving: boolean;
  saveError: string | null;
  onClose: () => void;
  onExpressionChange: (value: string) => void;
  onTestValueChange: (value: string) => void;
  onTest: () => void;
  onSave: () => void;
  onResetTest: () => void;
}

const RegexFormDialog = ({
  open,
  isEditMode,
  expression,
  testValue,
  testResult,
  isTestSuccessful,
  isTesting,
  isSaving,
  saveError,
  onClose,
  onExpressionChange,
  onTestValueChange,
  onTest,
  onSave,
  onResetTest,
}: RegexFormDialogProps) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEditMode ? "Edit Regular Expression" : "Add New Regular Expression"}
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Regular Expression"
          fullWidth
          value={expression}
          onChange={(e) => {
            onExpressionChange(e.target.value);
            onResetTest();
          }}
          placeholder="e.g., ^[A-Z0-9]+$"
          helperText="Enter a valid regular expression pattern"
          sx={{ mb: 2 }}
        />

        <TextField
          margin="dense"
          label="Test Value"
          fullWidth
          value={testValue}
          onChange={(e) => {
            onTestValueChange(e.target.value);
            onResetTest();
          }}
          placeholder="Enter a value to test against the expression"
          helperText="Provide a test value to validate your expression"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              onTest();
            }
          }}
          sx={{ mb: 2 }}
        />

        <Button
          variant="outlined"
          fullWidth
          onClick={onTest}
          disabled={!expression.trim() || !testValue.trim() || isTesting}
          startIcon={isTesting ? <CircularProgress size={16} /> : null}
          sx={{ mb: 2 }}
        >
          {isTesting ? "Testing..." : "Test Expression"}
        </Button>

        {testResult && (
          <Alert
            severity={
              testResult.isValid && testResult.matches ? "success" : "error"
            }
            sx={{ mb: 2 }}
          >
            {testResult.isValid && testResult.matches
              ? "Expression is valid! The test value matches the pattern."
              : testResult.message ||
                "Expression does not match the test value."}
          </Alert>
        )}

        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {saveError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined" disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={onSave}
          variant="contained"
          disabled={!expression.trim() || !isTestSuccessful || isSaving}
          startIcon={isSaving ? <CircularProgress size={16} /> : null}
        >
          {(() => {
            if (isSaving) return "Saving...";
            if (isEditMode) return "Update";
            return "Add";
          })()}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RegexFormDialog;
